import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Language } from '@/api/types';
import { Button } from '@/components/ui/button.tsx';
import { actionColor } from '@/lib/brandColor.ts';
import type { ContinueOption } from '../positionOptions';
import { ApplyFlowShell } from './ApplyFlowShell';
import { ContinueCard } from './ContinueCard';

interface ContinueGateProps {
  items: ContinueOption[];
  bgColor: string;
  inkColor: string;
  logoUrl: string;
  brandName?: string;
  language: Language;
  availableLanguages: Language[];
  onLanguageChange: (language: Language) => void;
  onContinue: (invitationKey: string) => void;
  onDismiss: () => void;
  /**
   * False when every open role already has an unfinished interview (or the
   * property has no open roles left). Dismissing would then lead to an empty
   * form or the no-data screen, so the escape hatch is hidden rather than
   * offered as a dead end.
   */
  canApplyElsewhere: boolean;
}

/**
 * The gate shown before the apply form when the visitor's session has
 * unfinished interviews. It is a screen, not a modal, and has no URL of its
 * own — `ApplyJob` swaps it for the form when dismissed.
 */
export function ContinueGate({
  items,
  bgColor,
  inkColor,
  logoUrl,
  brandName,
  language,
  availableLanguages,
  onLanguageChange,
  onContinue,
  onDismiss,
  canApplyElsewhere,
}: ContinueGateProps) {
  const { t } = useTranslation('applyJob');
  const single = items.length === 1;
  const brandColor = actionColor(bgColor);

  // Secondary escape hatch, in both the single and multi layouts. Deliberately
  // a small text button: it must stay clearly subordinate to continuing, which
  // is the action the screen exists to push.
  const dismissButton = canApplyElsewhere ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      data-testid="continue-gate-dismiss"
      className="mx-auto mt-1 h-8 w-auto px-3 text-[13px] font-semibold"
      style={{ color: single ? '#6b7078' : brandColor }}
      onClick={onDismiss}
    >
      {t('continue_gate_apply_different')}
    </Button>
  ) : null;

  return (
    <ApplyFlowShell
      bgColor={bgColor}
      inkColor={inkColor}
      logoUrl={logoUrl}
      brandName={brandName}
      welcomeTitle=""
      language={language}
      availableLanguages={availableLanguages}
      onLanguageChange={onLanguageChange}
      lockHero="compact"
      footer={
        <>
          {single && (
            <Button
              type="button"
              variant="brand"
              size="lg"
              className="w-full max-w-md gap-2 rounded-xl"
              onClick={() => onContinue(items[0].invitationKey)}
            >
              {t('continue_interview_button')}
              <ArrowRight className="size-4" aria-hidden />
            </Button>
          )}
          {dismissButton}
        </>
      }
    >
      <div className="mt-4 flex flex-col">
        <span
          className="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-extrabold tracking-widest"
          style={{ background: `${brandColor}16`, color: brandColor }}
        >
          <span
            className="size-1.5 rounded-full"
            style={{ background: brandColor }}
            aria-hidden
          />
          {t(single ? 'continue_gate_badge' : 'in_progress_group_label')}
        </span>

        <h1 className="mt-3.5 text-[23px] font-extrabold leading-tight tracking-tight text-[#16171a]">
          {t(single ? 'continue_gate_title_single' : 'continue_gate_title_multiple')}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[#6b7078]">
          {t(single ? 'continue_gate_subtitle_single' : 'continue_gate_subtitle_multiple')}
        </p>

        <div className="mt-5 flex flex-col gap-2.5">
          {items.map((item) => (
            <ContinueCard
              key={item.value}
              item={item}
              brandColor={brandColor}
              onContinue={
                single ? undefined : () => onContinue(item.invitationKey)
              }
            />
          ))}
        </div>
      </div>
    </ApplyFlowShell>
  );
}
